/**
 * Which position a pre-filled form is filled from: the client as they stand TODAY, or as they will
 * stand once a saved plan has been carried out (owner, 3 Oct 2026).
 *
 * "When we fill in the SA457, is this based on the current position or post strategy? ... allow the
 * user to do either in the event it was time critical (they were going to put mum/dad in immediately
 * and then restructure some assets after)." So every pre-filled Services Australia form offers both:
 *
 *   - today's position, to lodge now — and report each change within 14 days as the plan is done;
 *   - the position after a saved plan, to lodge once the changes are made — never before, because
 *     Services Australia assesses what you own and earn on the day it decides.
 *
 * No module saves the after-the-plan profile itself; each saves the plan's inputs. So the position is
 * rebuilt here with the SAME code each module models the plan with — its own applier, or the
 * engine's over the calculation API — so the form and the plan's figures cannot describe two
 * different households.
 */
import { applyFacilityMoveToFactFind, withExistingDepositRows } from '../../../../shared/src/calculation/support/facilityMove.ts';
import { allocateSecondDepositFunding } from '../../../../shared/src/calculation/support/secondDeposit.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import * as calculationApi from '../../services/calculationApi.ts';
import { simulateManualAgedCarePlan, debtPayoffPlanFrom } from '../agedCare/agedCarePlanFactFind.ts';
import { simulateManualSahPlan } from '../supportAtHome/sahPlanFactFind.ts';
import { applyStrategyToFactFind, type StrategyCandidate } from '../../strategy/SupportAtHomeStrategyApplicator.ts';
import { applyDownsizeToFactFind } from '../supportAtHome/supportAtHomeFactFindMutators.tsx';
import { buildSimulatedFactFindForVillageStrategy } from '../../../../shared/src/calculation/retirementVillageCalculations.ts';
import { applyDebtPayoffToFactFind } from '../../../../shared/src/calculation/support/debtPayoff.ts';
import { generateCashflowPensionCandidates } from '../cashflowPension/cashflowPensionCandidates.ts';
import { manualCandidateFrom, liquidSourcesOf, DEFAULT_ANNUITY_TERMS, EMPTY_ALLOCATION } from '../cashflowPension/cashflowPensionScenario.ts';

export type PlanType = 'aged_care' | 'home_care' | 'retirement_village' | 'downsizer' | 'granny_flat' | 'cashflow_pension';

/** A saved plan as App state holds it (each module's save shape; read loosely). */
export interface SavedPlan {
    id?: string;
    name?: string;
    type?: string;
    source?: string;
    createdAt?: string;
    inputs?: Record<string, any>;
    outputs?: Record<string, any>;
    strategy?: Record<string, any>;
}

const PLAN_TYPE_WORDS: Record<PlanType, string> = {
    aged_care: 'Aged care',
    home_care: 'Support at Home',
    retirement_village: 'Retirement village',
    downsizer: 'Downsizing',
    granny_flat: 'Granny flat',
    cashflow_pension: 'Cashflow and Age Pension',
};

export function planLabel(plan: SavedPlan): string {
    const kind = PLAN_TYPE_WORDS[plan.type as PlanType] || 'Plan';
    const when = plan.createdAt ? new Date(plan.createdAt) : null;
    const date = when && !Number.isNaN(when.getTime()) ? ` (saved ${when.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })})` : '';
    return `${kind}: ${plan.name || 'unnamed plan'}${date}`;
}

/** Saved plans of the given types, newest first: the one a client most likely means is at the top. */
export function plansOfTypes(saved: SavedPlan[] | undefined, types: readonly string[]): SavedPlan[] {
    return (saved || [])
        .filter((p) => p && types.includes(String(p.type)))
        .slice()
        .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
}

/** The Action Plan journey each module's plans belong to. */
export const PLAN_TYPE_FOR_WORKFLOW: Record<string, PlanType> = {
    aged_care: 'aged_care',
    home_care: 'home_care',
    retirement_village: 'retirement_village',
    downsize_home: 'downsizer',
    granny_flat: 'granny_flat',
};

/**
 * What a plan does that the profile has no field for, but the forms ask about. Renting out the home
 * is the case in point (owner, 5 Oct 2026): the plan models the rent as a setting, not as a change to
 * the profile, so without this an "after the plan" form still said the home was not let.
 */
export interface PlanFacts {
    /** The former home is let out under the plan: weekly rent, and its expenses a year. */
    homeRented?: { perWeek: number; expensesPerYear: number };
    /** The plan is a move into residential care. */
    enteringCare?: boolean;
}

export type PlanPosition =
    | { ok: true; factFind: DraftFactFind; caveats: string[]; facts: PlanFacts }
    | { ok: false; reason: string };

const n = (v: unknown): number => {
    const x = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
    return Number.isFinite(x) ? x : 0;
};

/** The facts a saved plan carries beyond the profile: where it records them, by module. */
export function planFactsOf(plan: SavedPlan): PlanFacts {
    const inputs = plan.inputs || {};
    const facts: PlanFacts = {};
    if (plan.type === 'aged_care') {
        facts.enteringCare = true;
        const engine = plan.strategy?.strategyOptions as Record<string, unknown> | undefined;
        if (engine?.rentHome) facts.homeRented = { perWeek: n(engine.rentAmount), expensesPerYear: n(engine.rentExpenses) };
        else if (inputs.overallStrategy?.rentHome) facts.homeRented = { perWeek: n(inputs.strategyInputs?.rentAmount), expensesPerYear: n(inputs.strategyInputs?.rentExpenses) };
    }
    if (plan.type === 'retirement_village') {
        const rentHome = inputs.rentHome ?? inputs.rvOverallStrategy?.rentHome ?? inputs.fundFrom === 'rent_home';
        if (rentHome && inputs.rentDetails) facts.homeRented = { perWeek: n(inputs.rentDetails.rentAmount), expensesPerYear: n(inputs.rentDetails.rentExpenses) };
    }
    return facts;
}

const asFactFind = (ff: DraftFactFind): FactFind => ff as unknown as FactFind;
const asDraft = (ff: unknown): DraftFactFind => ff as DraftFactFind;

/** Keys an applier adds for its own bookkeeping, which are not part of anyone's profile. */
function withoutBookkeeping(ff: DraftFactFind): DraftFactFind {
    const out = { ...(ff as Record<string, unknown>) };
    for (const k of Object.keys(out)) if (k.startsWith('__') && k !== '__appState' && k !== '__appData') delete out[k];
    return out as DraftFactFind;
}

/**
 * The client's profile once `plan` has been carried out. Never throws: a plan that cannot be rebuilt
 * says why, and the form falls back to today's position for the client to choose.
 */
export async function positionAfterPlan(ff: DraftFactFind, plan: SavedPlan): Promise<PlanPosition> {
    try {
        const r = await rebuild(ff, plan);
        if (!r.ok) return r;
        return { ...r, factFind: withoutBookkeeping(r.factFind), facts: planFactsOf(plan) };
    } catch {
        return { ok: false, reason: 'This plan could not be rebuilt just now. Use today’s position, or open the module and save the plan again.' };
    }
}

/**
 * A manual plan's second room (both of a couple entering care): its deposit is funded from what the
 * first room's funding leaves, exactly as the analyser worked it out, so the after-plan position
 * has both deposits gone from the savings.
 */
export function secondRoomFundingOf(ff: DraftFactFind, inputs: Record<string, any>): { secondRadFunding?: Record<string, number> } {
    const sr = inputs.secondRoom as { roomPrice?: number; radPaid?: number } | null | undefined;
    const amount = Math.min(Number(sr?.radPaid) || 0, Number(sr?.roomPrice) || 0);
    if (!sr || !(amount > 0)) return {};
    return { secondRadFunding: allocateSecondDepositFunding(ff as unknown as FactFind, amount, (inputs.radFunding || {}) as Record<string, number>).radFunding };
}

async function rebuild(ff: DraftFactFind, plan: SavedPlan): Promise<{ ok: true; factFind: DraftFactFind; caveats: string[] } | { ok: false; reason: string }> {
    const inputs = plan.inputs || {};
    const caveats: string[] = [];
    switch (plan.type) {
        case 'aged_care': {
            const row = plan.strategy;
            if (row && (row.radFunding || row.strategyOptions || row.meansTestStrategy)) {
                // The engine's own "at entry" balance sheet: the one its fees were assessed on. Where
                // both of a couple enter care, the row's second room goes too, so its deposit has left.
                const scenario = { radFunding: row.radFunding || {}, strategyOptions: row.strategyOptions || {}, meansTestStrategy: row.meansTestStrategy, ...(row.secondRoom ? { secondRoom: row.secondRoom } : {}) };
                return { ok: true, factFind: asDraft(await calculationApi.agedCareScenarioPositionFactFind(ff, scenario)), caveats };
            }
            if (plan.source === 'suggestion_engine') return { ok: false, reason: 'This plan was saved before plans recorded their details. Select it again in the aged care module to use it here.' };
            if (inputs.overallStrategy?.sellInvestmentProperty && !inputs.investmentPropertyDetails) {
                caveats.push('The investment property sale is not included: this plan was saved before its details were recorded. Save it again in the aged care module to include it.');
            }
            const { data } = simulateManualAgedCarePlan(ff, {
                scenarioLivingExpenses: inputs.scenarioLivingExpenses ?? ff?.expenses?.livingExpenses,
                overallStrategy: inputs.overallStrategy || {},
                downsizeDetails: inputs.downsizeDetails || {},
                investmentPropertyDetails: inputs.investmentPropertyDetails || null,
                reverseMortgageDetails: inputs.reverseMortgageDetails || null,
                otherStrategies: inputs.otherStrategies || {},
                annuityRecord: inputs.annuityRecord || null,
                radFunding: (inputs.radFunding || {}) as Record<string, number>,
                ...secondRoomFundingOf(ff, inputs),
                cgtOverrides: inputs.cgtOverrides || {},
                debtPayoffPlan: debtPayoffPlanFrom(inputs.debtPayments, inputs.debtFunding, inputs.debtRepaymentBasis),
                // After the plan: the deposit paid and the debt cleared, whatever day they fall on.
                // A change of home sells on the move day (the engine's job), so the simulation leaves the
                // sale to be applied with the move below.
                deferToEngine: !!inputs.facilityMove,
            });
            // A change of home (8 Oct 2026): after the plan the move has happened — the deposit held
            // (from the profile) refunded, the nominated assets sold, the new deposit paid.
            const mv = inputs.facilityMove as { person?: 'client' | 'partner'; radTarget?: number; refundToday?: number } | null | undefined;
            if (mv && (Number(mv.radTarget) || 0) >= 0 && inputs.facilityMove) {
                const opened = withExistingDepositRows(data as FactFind);
                const held = (opened.assets?.radPaid || []).filter((r) => (r.ownership ?? 'client') === (mv.person ?? 'client')).reduce((t, r) => t + (Number(r.value) || 0), 0);
                const moved = applyFacilityMoveToFactFind(opened, { person: mv.person ?? 'client', refundToday: held, radTarget: Number(mv.radTarget) || 0, radFunding: (inputs.radFunding || {}) as Record<string, number>, cgtOverrides: inputs.cgtOverrides || {} }).factFind;
                return { ok: true, factFind: asDraft(moved as never), caveats };
            }
            return { ok: true, factFind: asDraft(data), caveats };
        }
        case 'home_care': {
            if (inputs.seConfig) {
                return { ok: true, factFind: applyStrategyToFactFind(ff, inputs.seConfig as StrategyCandidate), caveats };
            }
            if (inputs.selectedFundingStrategy === 'annuity' && !inputs.annuityRecordForCalculation) {
                caveats.push('The annuity is not included: this plan was saved before its details were recorded. Save it again in the Support at Home module to include it.');
            }
            const beforeFinancials = await calculationApi.calculateFinancials(ff);
            const { optimizedFactFind } = simulateManualSahPlan(ff, {
                manualStrategies: { gifting: inputs.gifting ?? 0, funeralBonds: inputs.funeralBonds ?? 0, giftingSource: inputs.giftingSource, funeralSource: inputs.funeralSource },
                propertyStrategy: inputs.propertyStrategy,
                downsizeDetails: inputs.downsizeDetails || {},
                renovateDetails: inputs.renovateDetails || {},
                upgradeDetails: inputs.upgradeDetails || {},
                overallStrategy: inputs.overallStrategy || {},
                reverseMortgageDetails: inputs.reverseMortgageDetails || null,
                // A linked residential stay is the partner's care, not this assessment's position.
                selectedAgedCareIntegration: null,
                selectedFundingStrategy: inputs.selectedFundingStrategy,
                annuityRecordForCalculation: inputs.annuityRecordForCalculation || null,
                beforeFinancials,
            });
            return { ok: true, factFind: optimizedFactFind, caveats };
        }
        case 'retirement_village': {
            const villageInputs = inputs.villageInputs || {};
            const fundFrom = inputs.fundFrom || 'liquid';
            const rentHome = !!(inputs.rentHome ?? inputs.rvOverallStrategy?.rentHome);
            const rvStrategy = {
                fundFrom,
                rentHome,
                sellHomeDetails: fundFrom === 'sell_home' ? inputs.sellHomeDetails : undefined,
                rentDetails: rentHome ? inputs.rentDetails : undefined,
                entryFeeFunding: inputs.entryFeeFundingProvenance?.resolvedAllocation ?? inputs.entryFeeFunding ?? inputs.entryFeeFundingOverride,
                entryFeeCgtOverrides: inputs.entryFeeCgtOverrides,
                investmentPropertyDetails: fundFrom === 'investment_property' ? (inputs.rvInvestmentPropertyDetails ?? inputs.investmentPropertyDetails) : undefined,
                otherStrategies: inputs.otherStrategies,
                annuityRecord: inputs.annuityRecord,
            };
            let out = buildSimulatedFactFindForVillageStrategy(asFactFind(ff), rvStrategy, villageInputs).factFind as unknown;
            if (inputs.debtPayoff) out = applyDebtPayoffToFactFind(out as never, inputs.debtPayoff, {}).factFind;
            return { ok: true, factFind: asDraft(out), caveats };
        }
        case 'downsizer': {
            if (!inputs.details) return { ok: false, reason: 'This plan has no sale details recorded. Open the downsizing module and save it again.' };
            let out: unknown = applyDownsizeToFactFind(ff, inputs.details);
            if (inputs.debtPayoff) out = applyDebtPayoffToFactFind(out as never, inputs.debtPayoff, {}).factFind;
            return { ok: true, factFind: asDraft(out), caveats };
        }
        case 'granny_flat': {
            if (!inputs.gf) return { ok: false, reason: 'This plan has no arrangement recorded. Open the granny flat module and select it again.' };
            return { ok: true, factFind: asDraft(await calculationApi.buildSimulatedFactFindForGrannyFlat(ff, inputs.gf)), caveats };
        }
        case 'cashflow_pension': {
            let candidate = inputs.candidate as Record<string, unknown> | null | undefined;
            if (!candidate && inputs.source === 'manual' && inputs.allocation) {
                candidate = manualCandidateFrom({ ...EMPTY_ALLOCATION, ...inputs.allocation }, DEFAULT_ANNUITY_TERMS, liquidSourcesOf(ff)) as Record<string, unknown>;
                if (Number(inputs.allocation.annuity) > 0) caveats.push('The annuity is rebuilt on the default terms: this plan was saved before its terms were recorded.');
            }
            if (!candidate && inputs.engineOptionId) {
                candidate = (generateCashflowPensionCandidates(ff, null) as unknown as Array<Record<string, unknown>>).find((c) => c.id === inputs.engineOptionId) || null;
            }
            if (!candidate) return { ok: false, reason: 'This plan could not be rebuilt. Open the cashflow module and save it again.' };
            return { ok: true, factFind: asDraft(await calculationApi.cashflowPensionCandidatePositionFactFind(ff, candidate)), caveats };
        }
        default:
            return { ok: false, reason: 'This kind of plan does not change what the form asks.' };
    }
}

/** The Age Pension a plan expects (a year), as its save recorded it. */
export function planPension(plan: SavedPlan | null | undefined): number {
    const v = Number(plan?.outputs?.agePension);
    return Number.isFinite(v) ? v : 0;
}
